import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '../../../components/Button/Button'
import SelfEnrollmentCreditTables from '../components/SelfEnrollmentCreditTables'
import {
  runCreditCheck,
  getEnrollmentDebts,
  getCreditOptions,
} from '../../../features/selfEnrollment/selfEnrollment'
import { useMyContextLoading } from '../../../components/LoadingContextProvider/LoadingContextProvider'
import { showSnackBar } from '../../../components/Snackbar/Snackbar'
import { selectFeatureFlag } from '../../../features/featureFlags/featureFlags'
import { CREDIT_SELECT_MANUAL_POPUP_ON_REQUEST } from '../../../config/featureFlagKeys'

const DebtInfoStep = ({ selectedDebts, setSelectedDebts, onNext, onBack, useManualEntry, setUseManualEntry }) => {
  const dispatch = useDispatch()
  const liveFlag = useSelector(selectFeatureFlag(CREDIT_SELECT_MANUAL_POPUP_ON_REQUEST))
  const popupOnRequestRef = useRef(false)
  if (liveFlag) popupOnRequestRef.current = true
  const popupOnRequest = popupOnRequestRef.current
  const { triggerLoading } = useMyContextLoading()
  const {
    contactId,
    debts,
    creditCheckLoading,
    debtsLoading,
  } = useSelector(state => state.selfEnrollment)

  const [showTables, setShowTables] = useState(false)
  const debtsCheckedRef = useRef(false)

  useEffect(() => {
    if (contactId && !debtsCheckedRef.current) {
      debtsCheckedRef.current = true
      checkExistingDebts()
    }
  }, [contactId])

  const checkExistingDebts = async () => {
    const existingDebts = await dispatch(getEnrollmentDebts({ contactId }))
    if (existingDebts.payload?.data?.eligible?.length > 0 || existingDebts.payload?.data?.ineligible?.length > 0) {
      setShowTables(true)
      await dispatch(getCreditOptions())
    }
  }

  const handleRunCreditCheck = async () => {
    triggerLoading(true)
    const result = await dispatch(runCreditCheck({ contactId }))
    triggerLoading(false)

    if (runCreditCheck.fulfilled.match(result)) {
      setShowTables(true)
      await dispatch(getCreditOptions())
      if (result.payload.data.eligible.length === 0) {
        showSnackBar('info', 'No eligible accounts found. You can add debts manually.')
      }
    } else {
      showSnackBar('error', result.payload || 'Credit check failed')
    }
  }

  const handleManualEntry = async () => {
    setUseManualEntry(true)
    setShowTables(true)
    triggerLoading(true)
    await dispatch(getCreditOptions())
    triggerLoading(false)
  }

  const handleNext = () => {
    if (selectedDebts.length > 0) {
      onNext()
    } else {
      showSnackBar('error', 'Please select at least one debt')
    }
  }

  if (!contactId || debtsLoading) {
    return (
      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full p-6 md:p-12 text-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    )
  }

  if (showTables || useManualEntry) {
    return (
      <div className="w-full">
        <SelfEnrollmentCreditTables
          selectedCreditLines={selectedDebts}
          setSelectedCreditLines={setSelectedDebts}
          onNext={handleNext}
          onBack={onBack}
          useManualEntry={useManualEntry}
          popupOnRequest={popupOnRequest}
        />
      </div>
    )
  }

  return (
    <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full">
      <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
        Credit Check
      </div>
      <div className="border-b border-divider mx-4 md:mx-6" />
      <div className="px-4 md:px-12 py-4 md:py-6">
        <div className="bg-background rounded-2xl p-4 md:p-6 mb-4 md:mb-6">
          <p className="text-sm text-text-secondary">
            By clicking "Run Soft Credit Check", you authorize us to obtain information from your
            personal credit report from one or more credit bureaus. This is a soft inquiry
            and will not affect your credit score.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <Button
            width="w-full md:flex-1"
            onClick={handleRunCreditCheck}
            disabled={creditCheckLoading}
          >
            {creditCheckLoading ? 'Running Credit Check...' : 'Run Soft Credit Check'}
          </Button>
          <Button
            type="outlined"
            width="w-full md:flex-1"
            onClick={handleManualEntry}
          >
            Enter Debts Manually
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DebtInfoStep
